// src/scripts/altcha-loader.ts
//
// docs/visitor-check-choice_plan.md Phase 2, task 2.1: loads the self-hosted
// ALTCHA widget script, once per page, and only for a form whose effective
// visitor check is "builtin" -- so a form with no check, or Cloudflare's,
// pays nothing extra (benefits 1 and 3). The widget itself is vendored into
// public/js/altcha.js by scripts/build-altcha-widget.mjs (wired into
// `pretest` and `npm run build`), copied verbatim from the npm package, never
// fetched from a CDN: docs/visitor-check-choice_summary.md Task 0.1 confirmed
// the shipped bundle makes no third-party request of its own once self-hosted
// and pointed at our own challenge endpoint (src/pages/api/forms/[id]/altcha.ts).
//
// The widget is a Web Component that self-registers
// (`customElements.define("altcha-widget", ...)`) the moment its script runs
// -- confirmed by reading the shipped bundle -- so this loader's only job is
// to get that one `<script>` tag onto the page; it renders nothing itself and
// calls no render API, unlike Cloudflare Turnstile's imperative render call
// on `window.turnstile` (src/scripts/kiroku-embed.ts).
//
// Relative import, not the `@/` alias CLAUDE.md section 2 otherwise prefers:
// this file lives in src/scripts/ alongside kiroku-embed.ts, which every
// other import in that file already reaches with `../lib/...` because
// scripts/build-embed.mjs's esbuild options carry no `@` alias (unlike
// scripts/build-form-script.mjs's). A relative import works under both of
// this module's eventual bundling contexts (task 2.2); an `@/` one would not.
import type { VisitorCheck } from "../lib/utils/visitorCheck";

/**
 * Where scripts/build-altcha-widget.mjs vendors the widget, relative to our
 * own origin. Every renderer builds the script URL as
 * `${apiUrl}${ALTCHA_WIDGET_PATH}`, the same `apiUrl` base already used for
 * this embed's own CSS and submit endpoint (`loadCSS` in kiroku-embed.ts), so
 * the URL is always our domain, never a third party's.
 */
export const ALTCHA_WIDGET_PATH = "/js/altcha.js";

/**
 * Appends the widget's `<script>` tag pointed at our own origin, once per
 * page, and resolves once it has loaded. A no-op for any check other than
 * `"builtin"` -- the one place that guarantee is enforced, so a caller does
 * not have to repeat the condition (and cannot forget it, the way the
 * dashboard dropdown's three states could otherwise drift from what each
 * renderer actually loads).
 *
 * Never throws synchronously: script-load failure rejects the returned
 * promise so a caller can treat it as "the check could not run" (the plan's
 * diagram branch 3) and let the submission continue, rather than surface an
 * error to the visitor.
 */
export function loadAltchaWidget(
  check: VisitorCheck,
  apiUrl: string,
): Promise<void> {
  if (check !== "builtin") return Promise.resolve();

  const src = `${apiUrl}${ALTCHA_WIDGET_PATH}`;
  if (document.querySelector(`script[src="${src}"]`)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    // The vendor's own documented usage (`altcha`'s README, "Or load via
    // <script> tag": `<script async defer src="/altcha.js" type="module">`).
    // The shipped bundle has no import/export statement, so `type="module"`
    // is not strictly required for it to run, but matching the vendor's
    // recommendation keeps us on the one loading path they actually test.
    script.type = "module";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}
